// src/oura/merchant/InviteColleagueDialog.tsx
//
// "Invite a colleague", for a merchant on Settings > Manage Team Access. The
// flow the old Team page had: one work email, and Oura's own invitation goes
// out, valid 14 days. One tier, so there is no role to pick: everyone added
// sees and does what the owner does.

import { useEffect, useState, type FormEvent } from "react";
import { SendIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@component/dialog";
import { useSnackbar } from "@hook/use-snackbar";
import { PillButton } from "@oura/ui";
import { isEmail } from "@oura/onboarding/drafts";
import { AccessField } from "./AccessDrawer";
import { inviteToTeam, teamError } from "../team/teamApi";

export function InviteColleagueDialog({
    open,
    onOpenChange,
    existing,
    onSent,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Who is already on the workspace, to say so instead of sending again. */
    existing: Array<{ email: string; invited: boolean }>;
    onSent: () => void;
}) {
    const snackbar = useSnackbar();
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);

    // Each opening starts empty.
    useEffect(() => {
        if (open) {
            setEmail("");
            setError("");
        }
    }, [open]);

    const send = async (e: FormEvent) => {
        e.preventDefault();
        const address = email.trim().toLowerCase();
        if (!isEmail(address)) {
            setError("Enter an email address, like name@yourbusiness.com.");
            return;
        }
        const already = existing.find((m) => m.email.toLowerCase() === address);
        if (already && !already.invited) {
            setError(`${address} can already sign in.`);
            return;
        }
        setError("");
        setSending(true);
        try {
            await inviteToTeam(address);
            snackbar.showSuccess(`Invite sent to ${address}.`, "The link in it works for 14 days.");
            onSent();
            onOpenChange(false);
        } catch (err) {
            setError(teamError(err, "The invite didn't send. Try again."));
        } finally {
            setSending(false);
        }
    };

    // Not closable while the invite is on its way: an answer landing after a
    // close and reopen would shut the new dialog and report the old address.
    const handleOpenChange = (next: boolean) => {
        if (sending) return;
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Invite a colleague</DialogTitle>
                    <DialogDescription>
                        They get an email to set up their own login (the link works for 14 days). They'll see and do everything you can.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={send} noValidate className="space-y-4">
                    <AccessField
                        id="invite-colleague-email"
                        label="Their work email"
                        type="email"
                        autoComplete="off"
                        placeholder="name@yourbusiness.com"
                        value={email}
                        onChange={(v) => {
                            setEmail(v);
                            if (error) setError("");
                        }}
                        error={error}
                    />
                    <div className="flex justify-end gap-2">
                        <PillButton type="button" disabled={sending} onClick={() => handleOpenChange(false)}>
                            Cancel
                        </PillButton>
                        <PillButton type="submit" variant="primary" icon={SendIcon} busy={sending}>
                            Send invite
                        </PillButton>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
